import React, { useEffect, useState } from "react";
import { useUtilitiesOverview } from "../../../lib/hooks/useUtilitiesOverview";
import { getBillingDetails } from "../../../lib/api/customer-details";
import UtilitiesHero from "./UtilitiesHero";
import UtilityCard from "./UtilityCard";
import UtilitiesOverview from "./UtilitiesOverview";
import DashboardWrapper from "../../Shared/DashboardWrapper";
import WaterOverview from "./WaterOverview";
import WasteOverview from "./WasteOverview";
import ConnectivityOverview from "./ConnectivityOverview";
import BackLink from "./Common/BackLink";
import { getExternalLink } from "../../../lib/api/external-link";
import { getInvoiceSummaryFeatureFlag } from "../../../lib/api/invoice-summary";

const CARD_CONFIG = [
    {
        type: "Water",
        idSuffix: "water",
        manageLink: "/dashboard/water",
        infoLink: "https://everflowutilities.com/services/water",
    },
    {
        type: "Waste",
        idSuffix: "waste",
        manageLink: "/dashboard/waste",
        infoLink: "https://everflowutilities.com/services/waste",
    },
    {
        type: "Connectivity",
        idSuffix: "connectivity",
        manageLink: null,
        infoLink: "https://everflowutilities.com/services/broadband-phone",
    },
];

export default function MainDashboard() {
    const { data, loading, error } = useUtilitiesOverview();
    const [billingBalance, setBillingBalance] = useState(null);
    const [selectedUtility, setSelectedUtility] = useState(null);
    const [helpLink, setHelpLink] = useState("");
    const [invoiceSummaryFeature, setInvoiceSummaryFeature] = useState(false);

    useEffect(() => {
        if (selectedUtility !== null) return;
        getBillingDetails()
            .then((resp) => {
                setBillingBalance(resp?.data?.data?.balance ?? null);
            })
            .catch(() => {
                setBillingBalance(null);
            });
    }, [selectedUtility]);

    useEffect(() => {
        getExternalLink()
            .then((resp) => {
                setHelpLink(resp.data.faqLink);
            })
            .catch(() => {
                setHelpLink("");
            });
    }, []);

    useEffect(() => {
        getInvoiceSummaryFeatureFlag()
            .then((resp) => {
                setInvoiceSummaryFeature(resp.data.data);
            })
            .catch(() => {
                setInvoiceSummaryFeature(false);
            });
    }, []);

    if (loading) {
        return (
            <div className="main-dashboard" id="main-dashboard-loading">
                <p>Loading utilities…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main-dashboard" id="main-dashboard-error">
                <p className="main-dashboard__error">
                    Unable to load utilities overview.
                </p>
            </div>
        );
    }

    const utilitiesSummary = Array.isArray(data?.data?.utilities)
        ? data.data.utilities
        : [];
    const hasWaterOrWasteUtilities = utilitiesSummary.some(
        (u) => u.utilityType === "Water" || u.utilityType === "Waste"
    );
    const displayName = data?.data?.displayName ?? "Customer";

    if (!hasWaterOrWasteUtilities) {
        return (
            <DashboardWrapper helpLink={helpLink}>
                <div className="main-dashboard" id="main-dashboard-empty">
                    <UtilitiesHero customerName={displayName} />
                    <section
                        className="utilities-overview-card main-dashboard__empty"
                        id="main-dashboard-empty-state"
                    >
                        <h2 className="main-dashboard__empty-title">No active accounts found</h2>
                        <p className="main-dashboard__empty-text">
                            We couldn&apos;t find any water or waste services linked to this user yet.
                            If you think this is a mistake, please contact our support team.
                        </p>
                    </section>
                </div>
            </DashboardWrapper>
        );
    }

    const wasteUtility = utilitiesSummary.find((u) => u.utilityType === "Waste");
    const connectivityUtility = utilitiesSummary.find(
        (u) => u.utilityType === "Connectivity"
    );

    return (
        <DashboardWrapper helpLink={helpLink}>
            <div className="main-dashboard" id="main-dashboard">
                <UtilitiesHero customerName={displayName} />
                <div className={`overview-back-slot ${selectedUtility ? '' : 'overview-back-slot--hidden'}`}>
                    <BackLink onBack={() => setSelectedUtility(null)} />
                </div>
                <section className="main-dashboard__cards">
                    {CARD_CONFIG.map((card) => {
                        const utility = utilitiesSummary.find(
                            (u) => u.utilityType === card.type
                        );
                        const status = utility ? card.status || "Manage" : "FindOutMore";
                        return (
                            <UtilityCard
                                key={card.type}
                                type={card.type}
                                status={status}
                                manageLink={card.manageLink}
                                infoLink={card.infoLink}
                                idSuffix={card.idSuffix}
                                isActive={selectedUtility === card.type}
                                onSelect={setSelectedUtility}
                            />
                        );
                    })}
                </section>

                {selectedUtility === null ? (
                    <UtilitiesOverview utilities={utilitiesSummary} billingBalance={billingBalance} />
                ) : (
                    <>
                        {selectedUtility === "Water" && (
                            <WaterOverview
                                onBack={() => setSelectedUtility(null)}
                                helpLink={helpLink}
                            />
                        )}
                        {selectedUtility === "Waste" && (
                            <WasteOverview
                                utility={wasteUtility}
                                onBack={() => setSelectedUtility(null)}
                            />
                        )}
                        {/* {selectedUtility === 'Connectivity' && <ConnectivityOverview utility={connectivityUtility} />} */}
                    </>
                )}

                {/* Hide summaries when an utility is selected */}
                {!selectedUtility && utilitiesSummary.length > 1 && invoiceSummaryFeature && (
                    <div className="main-dashboard__cta" id="invoice-cta">
                        <div className="main-dashboard__cta-item">
                            <p className="m-0 cta-label">Download latest invoice summary:</p>
                            <button
                                id="btn-download-latest-invoice"
                                disabled
                                className="pink-pill w-260px"
                            >
                                Download PDF
                            </button>
                        </div>
                        <div className="main-dashboard__cta-item">
                            <p className="m-0 cta-label">View previous invoice summaries:</p>
                            <button
                                id="btn-view-previous-invoices"
                                disabled
                                className="pink-pill secondary w-260px"
                            >
                                Click Here
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
}
